import React from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import moment from 'utils/moment';
import { IconAlertTriangle } from 'TimeOffList/components/icons';
import type { IClosureAffectedAppointment } from 'services/types/shopClosure';

interface Props {
  open: boolean;
  appointments: IClosureAffectedAppointment[];
  onClose: () => void;
}

const fmtStart = (raw: string): string => {
  const m = moment(raw, 'MM/DD/YYYY hh:mm A');
  return m.isValid() ? m.format('ddd, MMM D, YYYY · h:mm A') : raw;
};

/** Lists booked appointments that fall on the closed days so staff can call to reschedule. */
const AffectedDialog: React.FC<Props> = ({ open, appointments, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    onOk={onClose}
    centered
    width={640}
    title={null}
    closable={false}
    cancelButtonProps={{ style: { display: 'none' } }}
    okText="Got it"
    zIndex={1100}
    styles={{
      content: { borderRadius: 14, padding: '20px 24px' },
      mask: { background: 'rgba(0,24,72,0.45)' },
    }}
  >
    <TitleRow>
      <IconAlertTriangle size={20} strokeWidth={2.2} color="#B45309" />
      <span className="title">Appointments on closed days</span>
    </TitleRow>
    <Sub>
      {appointments.length} {appointments.length === 1 ? 'appointment is' : 'appointments are'} booked on these
      days. The closure was saved. Please call the customers to reschedule.
    </Sub>
    <List>
      {appointments.map(a => (
        <Row key={a.id}>
          <div className="main">
            <span className="name">{a.customerName || 'Walk-in'}</span>
            <span className="phone">{a.customerPhone || '-'}</span>
          </div>
          <div className="meta">
            <span>{fmtStart(a.startTime)}</span>
            <span className="staff">{a.staffName || '-'}</span>
          </div>
        </Row>
      ))}
    </List>
  </Modal>
);

export default AffectedDialog;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .title { font-size: 18px; font-weight: 700; color: #92400E; }
`;

const Sub = styled.p`
  margin: 8px 0 14px;
  font-size: 13px;
  color: #475569;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 50vh;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #F2D48A;
  background: var(--warning-100, #FFF8E6);
  border-radius: 10px;
  .main, .meta { display: flex; flex-direction: column; gap: 2px; }
  .meta { text-align: right; }
  .name { font-size: 14px; font-weight: 700; color: #1D2129; }
  .phone { font-size: 13px; color: #92400E; }
  .staff { font-size: 12px; color: #475569; }
  span { font-size: 13px; color: #1D2129; }
`;
